import { isEqual } from 'lodash';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

import { AppPath } from '@/shared/constants/path';

const getRequiredColumns = (
  tableRequiredColumns: string[],
  columns: GridColDef[],
) => {
  return columns
    .filter((column) => tableRequiredColumns.includes(column.field))
    .sort(
      (a, b) =>
        tableRequiredColumns.indexOf(a.field) -
        tableRequiredColumns.indexOf(b.field),
    );
};

const getOptionalColumns = (
  tableRequiredColumns: string[],
  columns: GridColDef[],
) => {
  return columns.filter(
    (column) => !tableRequiredColumns.includes(column.field),
  );
};

const getInitialVisibilityModel = (
  optionalColumns: GridColDef[],
  requiredColumns: GridColDef[],
) => {
  const optionalVisibility = optionalColumns
    .map((column) => ({ [column.field]: false }))
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  const requiredVisibility = requiredColumns
    .map((column) => ({ [column.field]: true }))
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return { ...optionalVisibility, ...requiredVisibility };
};

type Args = {
  tableColumnsList: string[];
  tableRequiredColumns: string[];
  icon?: ReactNode;
  clickableColumn?: string;
  clickableColumnRedirect?: [AppPath, string | null];
};

const useTableColumnVisibility = ({
  tableColumnsList,
  tableRequiredColumns,
  icon,
  clickableColumn,
}: Args) => {
  const { t } = useTranslation(['forms', 'common']);

  const [columns, setColumns] = useState<GridColDef[]>([]);

  const [visibilityModel, setVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const tableColumnsListRef = useRef<string[] | null>(null);
  const tableRequiredColumnsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (!isEqual(tableColumnsListRef.current, tableColumnsList)) {
      tableColumnsListRef.current = tableColumnsList;

      const allColumns = [
        ...new Set([...tableColumnsList, ...tableRequiredColumns]),
      ];

      const columns = allColumns.map((key) => ({
        field: key,
        headerName: t('forms:' + key),
      }));

      const requiredColumns = getRequiredColumns(tableRequiredColumns, columns);
      const optionalColumns = getOptionalColumns(tableRequiredColumns, columns);
      const reorderedColumns = [...requiredColumns, ...optionalColumns];

      setColumns(reorderedColumns);

      setVisibilityModel((prev) => {
        return {
          ...prev,
          ...getInitialVisibilityModel(optionalColumns, requiredColumns),
        };
      });
    }
  }, [t, tableColumnsList, tableRequiredColumns, clickableColumn, icon]);

  useEffect(() => {
    if (!isEqual(tableRequiredColumns, tableRequiredColumnsRef.current)) {
      const allColumns = [
        ...new Set([...tableColumnsList, ...tableRequiredColumns]),
      ];

      const visibilityModelRequired = Object.entries(visibilityModel)
        .filter(([, value]) => value === true)
        .map(([key]) => key);

      const prevRequiredColumns = tableRequiredColumnsRef.current;

      const manualSetRequired = visibilityModelRequired.filter(
        (item) => prevRequiredColumns && !prevRequiredColumns.includes(item),
      );

      const newModelRequired = [
        ...allColumns.filter((column) => tableRequiredColumns.includes(column)),
        ...manualSetRequired,
      ]
        .map((column) => ({ [column]: true }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      const newModelOptional = allColumns
        .filter(
          (column) =>
            ![...tableRequiredColumns, ...manualSetRequired].includes(column),
        )
        .map((column) => ({ [column]: false }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      setVisibilityModel({
        ...newModelRequired,
        ...newModelOptional,
      });

      tableRequiredColumnsRef.current = tableRequiredColumns;
    }
  }, [tableRequiredColumns, tableColumnsList, visibilityModel]);

  return { columns, visibilityModel, setVisibilityModel };
};

export default useTableColumnVisibility;
